
import React, { useState } from "react";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { Users, Calendar, MapPin, Star, ArrowLeft, ArrowRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

const testimonials = [
{
  companyName: "Danone",
  companyLogo: "/lovable-uploads/danone-logo.png",
  logoClassName: "h-11",
  quote: "10/10. Les équipes ont été réactives et toujours arrangeantes. Bravo!",
  author: "Assistant Finance Operations, Danone",
  participants: 12,
  days: 4,
  location: "Saint-Germain-en-Laye",
  rating: 5,
  image: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Branding%20GC/Corporate-event-decor-ideas21-scaled.jpg"
},
{
  companyName: "Google",
  companyLogo: "/lovable-uploads/google-logo-testimonial.webp",
  quote: "Vous nous avez fait gagner un temps précieux dans la recherche, et votre flexibilité dans l'organisation a été remarquable. Nous referons appel à vous sans hésiter.",
  author: "Strategic Partner Manager, Google",
  participants: 10,
  days: 2,
  location: "Villerville",
  rating: 5,
  image: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Branding%20GC/Oliver-Bonacini-Carlu-Event-1024x1024.jpg"
},
{
  companyName: "Microsoft",
  companyLogo: "/lovable-uploads/microsoft-logo.png",
  quote: "C'était vraiment génial ! Tout s'est déroulé à la perfection.",
  author: "Assistante de direction, Microsoft",
  participants: 13,
  days: 1,
  location: "Paris",
  rating: 5,
  image: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Branding%20GC/hotel-du-compte2.jpeg"
},
{
  companyName: "Uber",
  companyLogo: "/lovable-uploads/uber-logo-testimonial.png",
  quote: "Nous vous recommandons vivement. Nous avons l'habitude de travailler avec vous et tout se passe toujours très bien.",
  author: "Strategy & Planning, Uber",
  participants: 28,
  days: 3,
  location: "La Clusaz",
  rating: 5,
  image: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Branding%20GC/9079-deyoung-211105-drew-altizer.jpg"
},
{
  companyName: "Figma",
  companyLogo: "/lovable-uploads/figma-logo-testimonial.webp",
  logoClassName: "h-14",
  quote: "Génial ! L'offsite était excellent, une super expérience pour l'équipe.",
  author: "Équipe GTM Leadership, Figma",
  participants: 10,
  days: 2,
  location: "Zossen",
  rating: 4.5,
  image: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Branding%20GC/BeachclUP-1-4.webp"
},
{
  companyName: "Amazon",
  companyLogo: "/lovable-uploads/amazon-logo-testimonial.webp",
  quote: "Le lieu était parfait.",
  author: "Account Executive, Amazon",
  participants: 100,
  days: 1,
  location: "Paris",
  rating: 4.5,
  image: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/20190914_210423.jpg"
},
{
  companyName: "J.P.Morgan",
  companyLogo: "/lovable-uploads/jpmorgan-logo.png",
  quote: "Tout le monde était satisfait, ça s'est vraiment très bien passé !",
  author: "Banquier privé, JP Morgan",
  participants: 54,
  days: 2,
  location: "Nice",
  rating: 5,
  image: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Capture%20d%E2%80%99%C3%A9cran%202025-06-17%20%C3%A0%2015.53.26.png"
},
{
  companyName: "HubSpot",
  companyLogo: "/lovable-uploads/hubspot-logo.png",
  quote: "Le lieu était exceptionnel ! Merci pour tout, j'ai hâte de retravailler avec vous. Tout a été organisé très rapidement et je n'aurais pas pu y arriver sans l'équipe Naboo 🙂",
  author: "Event Marketing Manager, HubSpot",
  participants: 45,
  days: 1,
  location: "London",
  rating: 4.5,
  image: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Branding%20GC/1z4a0360.jpg.webp"
}];


const RatingDisplay = ({ rating }: {rating: number;}) =>
<div className="flex items-center gap-1">
    <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
    <span className="text-muted-foreground text-base">{rating}/5</span>
  </div>;


const TestimonialsSection = () => {
  const [startIndex, setStartIndex] = useState(0);
  const [direction, setDirection] = useState(1);

  const handlePrev = () => {
    setDirection(-1);
    setStartIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const handleNext = () => {
    setDirection(1);
    setStartIndex((prev) => (prev + 1) % testimonials.length);
  };

  const visibleCards = [
  testimonials[startIndex],
  testimonials[(startIndex + 1) % testimonials.length]];


  const slideVariants = {
    enter: (dir: number) => ({ x: dir > 0 ? 80 : -80, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir: number) => ({ x: dir > 0 ? -80 : 80, opacity: 0 })
  };

  const renderCard = (t: typeof testimonials[0], i: number) =>
  <motion.div
    key={`${startIndex}-${i}`}
    custom={direction}
    variants={slideVariants}
    initial="enter"
    animate="center"
    exit="exit"
    transition={{ duration: 0.3, ease: "easeInOut" }}
    className="flex-1 min-h-[360px] bg-background rounded-2xl border border-border overflow-hidden flex">
    
      <div className="flex-1 p-6 flex flex-col justify-between min-w-0">
        <div>
          <div className="h-12 flex items-center mb-3">
            <img src={t.companyLogo} alt={t.companyName} className={`${t.logoClassName || 'h-7'} max-w-[160px] object-contain`} />
          </div>
          <p className="text-foreground/80 leading-relaxed mb-2 text-base">
            "{t.quote}"
          </p>
          <p className="text-muted-foreground text-sm">{t.author}</p>
        </div>
        <div className="flex flex-col gap-2 mt-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <Users className="h-3.5 w-3.5" />
              <span className="text-sm">{t.participants} participants</span>
            </div>
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <Calendar className="h-3.5 w-3.5" />
              <span className="text-sm">{t.days} {t.days > 1 ? 'jours' : 'jour'}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <MapPin className="h-3.5 w-3.5" />
              <span className="text-sm">{t.location}</span>
            </div>
            <RatingDisplay rating={t.rating} />
          </div>
        </div>
      </div>
      <div className="w-[240px] flex-shrink-0">
        <img
        src={t.image}
        alt={`Événement ${t.companyName}`}
        loading="lazy"
        className="w-full h-full object-cover" />
      
      </div>
    </motion.div>;


  return (
    <section className="py-20 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12 text-center">
          <h2 className="text-4xl md:text-5xl font-normal text-foreground">
            Récemment organisés avec Naboo
          </h2>
        </div>

        {/* Desktop: 2 visible cards with nav arrows */}
        <div className="hidden md:block relative">
          <button
            onClick={handlePrev}
            className="absolute -left-5 top-1/2 -translate-y-1/2 z-10 bg-white hover:bg-gray-50 rounded-full p-2 shadow-md border border-gray-200 transition-all"
            aria-label="Précédent">
            
            <ArrowLeft className="h-4 w-4" />
          </button>
          <button
            onClick={handleNext}
            className="absolute -right-5 top-1/2 -translate-y-1/2 z-10 bg-white hover:bg-gray-50 rounded-full p-2 shadow-md border border-gray-200 transition-all"
            aria-label="Suivant">
            
            <ArrowRight className="h-4 w-4" />
          </button>
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div key={startIndex} className="flex gap-6">
              {visibleCards.map((t, i) => renderCard(t, i))}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Mobile: carousel */}
        <div className="md:hidden">
          <Carousel opts={{ align: "start", loop: true }} className="w-full">
            <CarouselContent className="-ml-3">
              {testimonials.map((t, i) =>
              <CarouselItem key={i} className="pl-3 basis-[90%]">
                  <div className="bg-background rounded-2xl border border-border overflow-hidden">
                    <div className="h-48 w-full">
                      <img
                      src={t.image}
                      alt={`Événement ${t.companyName}`}
                      loading="lazy"
                      className="w-full h-full object-cover" />
                    
                    </div>
                    <div className="p-5">
                      <div className="h-12 flex items-center mb-3">
                        <img src={t.companyLogo} alt={t.companyName} className={`${t.logoClassName || 'h-6'} max-w-[140px] object-contain`} />
                      </div>
                      <p className="text-foreground/80 text-sm leading-relaxed mb-2">
                        "{t.quote}"
                      </p>
                      <p className="text-muted-foreground text-xs mb-4">{t.author}</p>
                      <div className="flex flex-col gap-1.5">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <Users className="h-3 w-3" />
                            <span>{t.participants} participants</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <Calendar className="h-3 w-3" />
                            <span>{t.days} {t.days > 1 ? 'jours' : 'jour'}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <MapPin className="h-3 w-3" />
                            <span>{t.location}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                            <span className="text-xs text-muted-foreground">{t.rating}/5</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              )}
            </CarouselContent>
          </Carousel>
        </div>
      </div>
    </section>);

};

export default TestimonialsSection;